import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, ExternalLink, Star, X, Search, User } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

interface ProviderProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  bio: string | null;
  avatar_url: string | null;
}

interface PortfolioItem {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
}

interface Review {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
}

interface ProviderWithDetails extends ProviderProfile {
  portfolio: PortfolioItem[];
  reviews: Review[];
  averageRating: number;
  reviewCount: number;
}

interface ProviderSelectorProps {
  selectedProviderId: string | null;
  onProviderChange: (providerId: string | null) => void;
  currentUserId: string;
  onBeforeNavigate?: () => void;
}

export const ProviderSelector: React.FC<ProviderSelectorProps> = ({
  selectedProviderId,
  onProviderChange,
  currentUserId,
  onBeforeNavigate,
}) => {
  const [providers, setProviders] = useState<ProviderWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProviders();
  }, [currentUserId]);

  const fetchProviders = async () => {
    setIsLoading(true);
    try {
      // Fetch all profiles except current user
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, bio, avatar_url')
        .neq('id', currentUserId)
        .order('first_name');

      if (profilesError) throw profilesError;

      // Fetch portfolio items for all providers
      const { data: portfolioItems, error: portfolioError } = await supabase
        .from('portfolio_items')
        .select('id, user_id, title, description, image_url');

      if (portfolioError) throw portfolioError;

      // Fetch reviews for all providers
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('id, provider_id, rating, comment, created_at');

      if (reviewsError) throw reviewsError;

      // Combine data
      const providersWithDetails: ProviderWithDetails[] = (profiles || []).map(profile => {
        const userPortfolio = (portfolioItems || []).filter(p => p.user_id === profile.id);
        const userReviews = (reviews || []).filter(r => r.provider_id === profile.id);
        const avgRating = userReviews.length > 0 
          ? userReviews.reduce((sum, r) => sum + r.rating, 0) / userReviews.length 
          : 0;

        return {
          ...profile,
          portfolio: userPortfolio,
          reviews: userReviews,
          averageRating: avgRating,
          reviewCount: userReviews.length,
        };
      });

      setProviders(providersWithDetails);
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDisplayName = (provider: ProviderProfile) => {
    if (provider.first_name || provider.last_name) {
      return `${provider.first_name || ''} ${provider.last_name || ''}`.trim();
    }
    return 'Anonymous User';
  };

  const getInitials = (provider: ProviderProfile) => {
    const first = provider.first_name?.[0] || '';
    const last = provider.last_name?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  };

  const filteredProviders = providers.filter(provider => {
    const name = getDisplayName(provider).toLowerCase();
    const bio = (provider.bio || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query) || bio.includes(query);
  });

  const selectedProvider = providers.find(p => p.id === selectedProviderId);

  const handleViewProfile = (e: React.MouseEvent, providerId: string) => {
    e.stopPropagation();
    // Save form state before navigating
    onBeforeNavigate?.();
    navigate(`/profile/${providerId}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Send directly to a provider (optional)</label>
        {selectedProviderId && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onProviderChange(null)}
            className="h-auto py-1 px-2 text-xs"
          >
            <X className="h-3 w-3 mr-1" />
            Clear selection
          </Button>
        )}
      </div>

      {selectedProvider ? (
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-accent/50">
          <Avatar className="h-10 w-10">
            <AvatarImage src={selectedProvider.avatar_url || undefined} />
            <AvatarFallback>{getInitials(selectedProvider)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{getDisplayName(selectedProvider)}</p>
            <p className="text-xs text-muted-foreground">
              Request will be sent directly to this provider
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onProviderChange(null)}
          >
            Change
          </Button>
        </div>
      ) : (
        <div className="border rounded-lg">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search providers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
          </div>
          
          <ScrollArea className="h-64">
            {filteredProviders.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No providers found</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredProviders.map((provider) => (
                  <Collapsible
                    key={provider.id}
                    open={expandedId === provider.id}
                    onOpenChange={(open) => setExpandedId(open ? provider.id : null)}
                  >
                    <CollapsibleTrigger asChild>
                      <button
                        type="button"
                        className="w-full flex items-center gap-3 p-3 hover:bg-accent/50 transition-colors text-left"
                      >
                        <Avatar className="h-9 w-9 flex-shrink-0">
                          <AvatarImage src={provider.avatar_url || undefined} />
                          <AvatarFallback>{getInitials(provider)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{getDisplayName(provider)}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {provider.reviewCount > 0 && (
                              <span className="flex items-center gap-1">
                                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                {provider.averageRating.toFixed(1)} ({provider.reviewCount})
                              </span>
                            )}
                            {provider.portfolio.length > 0 && (
                              <span>{provider.portfolio.length} portfolio items</span>
                            )}
                          </div>
                        </div>
                        {expandedId === provider.id ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        )}
                      </button>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      <div className="px-3 pb-3 space-y-3 bg-muted/30">
                        {/* Bio */}
                        {provider.bio && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">About</p>
                            <p className="text-sm line-clamp-2">{provider.bio}</p>
                          </div>
                        )}

                        {/* Portfolio preview */}
                        {provider.portfolio.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Portfolio</p>
                            <div className="flex gap-2 overflow-x-auto pb-1">
                              {provider.portfolio.slice(0, 3).map((item) => (
                                <div key={item.id} className="flex-shrink-0 w-20">
                                  {item.image_url ? (
                                    <img
                                      src={item.image_url}
                                      alt={item.title}
                                      className="w-20 h-14 object-cover rounded"
                                    />
                                  ) : (
                                    <div className="w-20 h-14 bg-muted rounded flex items-center justify-center">
                                      <span className="text-xs text-muted-foreground">No image</span>
                                    </div>
                                  )}
                                  <p className="text-xs truncate mt-1">{item.title}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Reviews preview */}
                        {provider.reviews.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Recent Review</p>
                            <div className="bg-background rounded p-2">
                              <div className="flex items-center gap-1 mb-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`h-3 w-3 ${
                                      i < provider.reviews[0].rating
                                        ? 'fill-yellow-400 text-yellow-400'
                                        : 'text-muted-foreground'
                                    }`}
                                  />
                                ))}
                              </div>
                              {provider.reviews[0].comment && (
                                <p className="text-xs line-clamp-2">{provider.reviews[0].comment}</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="flex-1 h-8 text-xs"
                            onClick={(e) => handleViewProfile(e, provider.id)}
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View Profile
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            className="flex-1 h-8 text-xs"
                            onClick={() => onProviderChange(provider.id)}
                          >
                            Select Provider
                          </Button>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

export default ProviderSelector;
